import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { useLanguage } from "../contexts/LanguageContext";
import { useTheme } from "../contexts/ThemeContext";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Skeleton } from "../components/ui/skeleton";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import { 
  Package, 
  Lock, 
  Globe, 
  Sun, 
  Moon, 
  User,
  ShoppingCart,
} from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PublicStorePage() {
  const { storeSlug } = useParams();
  const { t, language, changeLanguage, languages } = useLanguage();
  const { theme, toggleTheme } = useTheme();

  const [store, setStore] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchStore();
  }, [storeSlug]);

  const fetchStore = async () => {
    try {
      const { data } = await axios.get(`${API}/store/${storeSlug}`);
      setStore(data);
    } catch (err) {
      setError(t("store.storeNotFound"));
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount, currency) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency,
    }).format(amount);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <header className="border-b border-border">
          <div className="max-w-6xl mx-auto px-4 h-16 flex items-center justify-between">
            <Skeleton className="h-8 w-32" />
            <div className="flex gap-2">
              <Skeleton className="h-10 w-10 rounded-xl" />
              <Skeleton className="h-10 w-10 rounded-xl" />
            </div>
          </div>
        </header>
        <section className="py-16 bg-gradient-to-br from-[#0B1F3A] to-[#3A6D8C]">
          <div className="max-w-6xl mx-auto px-4 text-center">
            <Skeleton className="w-24 h-24 rounded-full mx-auto mb-6" />
            <Skeleton className="h-10 w-48 mx-auto mb-4" />
            <Skeleton className="h-6 w-64 mx-auto" />
          </div>
        </section>
        <main className="max-w-6xl mx-auto px-4 py-12">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-8">
            {[...Array(3)].map((_, i) => (
              <Skeleton key={i} className="h-80 rounded-2xl" />
            ))}
          </div>
        </main>
      </div>
    );
  }

  if (error || !store) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="max-w-md w-full border-border">
          <CardContent className="p-10 text-center">
            <div className="w-20 h-20 rounded-2xl bg-muted flex items-center justify-center mx-auto mb-6">
              <User className="h-10 w-10 text-muted-foreground" />
            </div>
            <h1 className="text-2xl font-bold mb-3">{t("store.storeNotFound")}</h1>
            <p className="text-muted-foreground">
              {t("store.storeNotFoundDesc")}
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="public-store-page">
      {/* Header */}
      <header className="border-b border-border sticky top-0 bg-background/80 backdrop-blur-xl z-50">
        <div className="max-w-6xl mx-auto px-4 h-16 flex items-center justify-between">
          <span className="text-xl font-black tracking-tight bg-gradient-to-r from-[#0B1F3A] to-[#3A6D8C] bg-clip-text text-transparent dark:from-white dark:to-slate-300">
            Ellalink
          </span>
          <div className="flex items-center gap-2">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="rounded-xl">
                  <Globe className="h-5 w-5" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {languages.map((lang) => (
                  <DropdownMenuItem
                    key={lang.code}
                    onClick={() => changeLanguage(lang.code)}
                    className={`cursor-pointer ${language === lang.code ? "bg-muted" : ""}`}
                  >
                    <span className="mr-2">{lang.flag}</span>
                    {lang.name}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
            <Button variant="ghost" size="icon" onClick={toggleTheme} className="rounded-xl">
              {theme === "light" ? (
                <Moon className="h-5 w-5" />
              ) : (
                <Sun className="h-5 w-5" />
              )}
            </Button>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section
        className="relative py-20"
        style={{
          background: "linear-gradient(135deg, #0B1F3A 0%, #1A365D 50%, #3A6D8C 100%)",
        }}
      >
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-10 left-20 w-80 h-80 bg-blue-400/10 rounded-full blur-3xl" />
          <div className="absolute bottom-10 right-20 w-96 h-96 bg-cyan-400/10 rounded-full blur-3xl" />
        </div>
        <div className="relative z-10 max-w-6xl mx-auto px-4 text-center text-white">
          <div className="w-20 h-20 sm:w-28 sm:h-28 rounded-2xl bg-white/10 backdrop-blur-sm flex items-center justify-center mx-auto mb-6 sm:mb-8 text-2xl sm:text-4xl font-bold shadow-2xl">
            {store.creator.name?.charAt(0).toUpperCase() || "C"}
          </div>
          <h1 className="text-2xl sm:text-4xl md:text-5xl font-black tracking-tight mb-4">
            {store.creator.name}
          </h1>
          {store.creator.bio && (
            <p className="text-base sm:text-xl text-white/80 max-w-xl mx-auto">
              {store.creator.bio}
            </p>
          )}
        </div>
      </section>

      {/* Products Section */}
      <main className="max-w-6xl mx-auto px-4 py-8 sm:py-12 md:py-16">
        <div className="flex items-center justify-between mb-6 sm:mb-8">
          <h2 className="text-2xl font-bold">{t("store.products")}</h2>
          <span className="text-muted-foreground">
            {store.products?.length || 0} {t("store.items")}
          </span>
        </div>

        {store.products?.length === 0 ? (
          <Card className="border-border border-dashed">
            <CardContent className="py-20 text-center">
              <div className="w-20 h-20 rounded-2xl bg-muted flex items-center justify-center mx-auto mb-6">
                <Package className="h-10 w-10 text-muted-foreground" />
              </div>
              <h3 className="text-xl font-semibold mb-2">{t("store.noProducts")}</h3>
              <p className="text-muted-foreground">
                {t("store.noProductsCreator")}
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 lg:gap-8">
            {store.products.map((product, index) => (
              <a
                key={product.id}
                href={`/p/${product.slug}`}
                className="group block"
              >
                <Card
                  className="border-border overflow-hidden hover:shadow-2xl hover:shadow-[#0B1F3A]/10 dark:hover:shadow-white/5 transition-all duration-300 hover:-translate-y-1"
                  style={{ animationDelay: `${index * 50}ms` }}
                >
                  {/* Preview Image */}
                  <div className="aspect-video bg-gradient-to-br from-[#0B1F3A]/10 to-[#3A6D8C]/10 relative overflow-hidden">
                    <div className="absolute inset-0 flex items-center justify-center">
                      <Package className="h-14 w-14 text-muted-foreground/30 group-hover:scale-110 transition-transform duration-300" />
                    </div>
                    {product.blur_preview && (
                      <div className="absolute inset-0 backdrop-blur-md bg-black/5 flex items-center justify-center">
                        <div className="bg-black/50 backdrop-blur-sm rounded-xl px-4 py-2 flex items-center gap-2">
                          <Lock className="h-5 w-5 text-white" />
                          <span className="text-white font-medium">Premium</span>
                        </div>
                      </div>
                    )}
                  </div>

                  <CardContent className="p-6">
                    <h3 className="font-semibold text-lg truncate group-hover:text-primary transition-colors">
                      {product.title}
                    </h3>
                    {product.description && (
                      <p className="text-sm text-muted-foreground mt-2 line-clamp-2">
                        {product.description}
                      </p>
                    )}
                    <div className="flex items-center justify-between mt-6 pt-4 border-t border-border">
                      <span className="text-2xl font-bold">
                        {formatCurrency(product.price, product.currency)}
                      </span>
                      <Button 
                        size="sm" 
                        className="rounded-xl shadow-lg shadow-primary/20 group-hover:shadow-xl group-hover:shadow-primary/30 transition-all duration-300"
                      >
                        <ShoppingCart className="h-4 w-4 mr-2" />
                        {product.button_text || t("store.buyNow")}
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </a>
            ))}
          </div>
        )}
      </main>

      {/* Footer */}
      <footer className="border-t border-border py-8 mt-8">
        <div className="max-w-6xl mx-auto px-4 text-center text-sm text-muted-foreground">
          {t("store.poweredBy")} <span className="font-bold text-foreground">Ellalink</span>
        </div>
      </footer>
    </div>
  );
}
